import type { DTO } from "@equidefi/shared/types/dto";
import { ApiClient } from "./ApiClient";

class AgreementClient extends ApiClient {
  async docusealTemplates() {
    const response = await this.get("/v2/agreements/templates/docuseal");
    return response.data;
  }

  async docusealTemplate(id?: string) {
    const response = await this.get(`/v2/agreements/templates/docuseal/${id}`);
    return response.data;
  }

  async createSubmission(investmentId: string, manualSetup = false) {
    const response = await this.post("/v2/agreements/signatures/submission", {
      investment_id: investmentId,
      manual_setup: manualSetup,
    });
    return response.data;
  }

  async getUserToken(investmentAgreementId: string) {
    const response = await this.get(`/v2/agreements/signers/${investmentAgreementId}/token`);
    return response.data;
  }

  async getSigningPartyToken(investmentAgreementId: string, signingPartyId: string) {
    const response = await this.get(`/v2/agreements/signers/${investmentAgreementId}/token/${signingPartyId}`);
    return response.data;
  }

  async downloadAgreement(id: string, investmentAgreementId: string) {
    const response = await this.get(`/v2/agreements/investments/${id}/${investmentAgreementId}/download`);
    return response.data;
  }

  async offeringAgreements(id: string) {
    const response = await this.get(`/v2/agreements/offerings/${id}`);
    return response.data;
  }

  async deleteOfferingAgreement(id: string, agreementId: string) {
    const response = await this.delete(`/v2/agreements/offerings/${id}/${agreementId}`);
    return response.data;
  }

  async createOfferingAgreement(id: string, data: Record<string, any>) {
    const response = await this.post(`/v2/agreements/offerings/${id}`, data);
    return response.data;
  }

  async updateOfferingAgreement(id: string, offeringAgreementId: string, data: Record<string, any>) {
    const response = await this.patch(`/v2/agreements/offerings/${id}/agreements/${offeringAgreementId}`, data);
    return response.data;
  }

  async publishOfferingAgreement(id: string, offeringAgreementId: string) {
    const response = await this.post(`/v2/agreements/offerings/${id}/agreements/${offeringAgreementId}/publish`);
    return response.data;
  }

  async createDraftFromOfferingAgreement(id: string, offeringAgreementId: string) {
    const response = await this.post(`/v2/agreements/offerings/${id}/agreements/${offeringAgreementId}/draft`);
    return response.data;
  }

  async createOfferingAgreementPreview(
    id: string,
    offeringAgreementId: string,
    signers: Array<{ name: string; email: string }>,
  ) {
    const response = await this.post(`/v2/agreements/offerings/${id}/agreements/${offeringAgreementId}/preview`, {
      signers,
    });
    return response.data;
  }

  async investmentSignatures(id: string) {
    const response = await this.get(`/v2/agreements/investments/${id}`);
    return response.data;
  }

  async pingNextSecondarySigner(id: string) {
    const response = await this.post(`/v2/agreements/investments/${id}/ping`);
    return response.data;
  }

  async countersignInvestment(id: string) {
    const response = await this.post(`/v2/agreements/investments/${id}/countersign`);
    return response.data;
  }

  async cancelAgreement(investmentId: string, investmentAgreementId: string, reason: string, notify_investor: boolean) {
    const response = await this.delete(`/v2/agreements/investments/${investmentId}/${investmentAgreementId}`, {
      data: { reason, notify_investor },
    });

    return response.data;
  }

  async additionalSignerDetails(investmentAgreementId: string, signatureId: string) {
    const response = await this.get(`/v2/agreements/signers/${investmentAgreementId}/${signatureId}`);

    return response.data;
  }

  async agreementHistory(
    offeringId: string,
    agreementId: string,
    page = 1,
    perPage = 5,
  ): Promise<{
    data: DTO.AgreementHistory[];
    pagination: DTO.Pagination;
  }> {
    const response = await this.get(`/v2/agreements/offerings/${offeringId}/history/${agreementId}`, {
      params: { page, perPage },
    });
    return response.data;
  }

  async agreementHistoryDocument(
    offeringId: string,
    agreementId: string,
    historyId: string,
  ): Promise<DTO.AgreementHistoryDocument> {
    const response = await this.get(`/v2/agreements/offerings/${offeringId}/history/${agreementId}/${historyId}`);
    return response.data;
  }

  async createAgreementHistory(
    offeringId: string,
    agreementId: string,
    data: { document?: object; diff?: object; action?: string },
  ) {
    const response = await this.post(`/v2/agreements/offerings/${offeringId}/history/${agreementId}`, data);
    return response.data;
  }
}

export default AgreementClient;
